import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginationInput, SortInput } from "../graphql";
import { track, type ListName } from "../utils/analytics";

/** Long enough that typing a search term is one `list_filtered`, not one per keystroke. */
const FILTER_TRACK_DEBOUNCE_MS = 1000;

interface Args<Filter> {
  defaultSort?: SortInput;
  defaultPagination?: PaginationInput;
  defaultFilter?: Filter;
  prefix?: string;
  /** Names the list in analytics. Omit to leave sort/filter/paging untracked. */
  name?: ListName;
}

export interface ListVariables<Filter> {
  sort: SortInput;
  setSort: (sort: SortInput) => void;
  pagination: { page: number; perPage: number };
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  filter?: Filter;
  setFilter: (filter: Filter) => void;
}

function paramsToObject(search: URLSearchParams) {
  const result: Record<string, string> = {};
  for (const [key, value] of search.entries()) {
    result[key] = value;
  }
  return result;
}

function useListVariables<Filter>({
  defaultSort,
  defaultPagination,
  defaultFilter,
  prefix = "",
  name,
}: Args<Filter>): ListVariables<Filter> {
  const [search, setSearch] = useSearchParams();
  const filterTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(filterTimer.current), []);

  const trackFilter = (newFilter: Filter) => {
    if (!name) return;
    clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => {
      const active = Object.entries(newFilter ?? {}).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      );
      const query = (newFilter as { q?: unknown } | undefined)?.q;
      track("list_filtered", {
        list: name,
        filter_keys: active.map(([key]) => key).sort(),
        // Length only: a search term is usually a customer's name or phone number.
        query_length: typeof query === "string" ? query.length : 0,
      });
    }, FILTER_TRACK_DEBOUNCE_MS);
  };

  const rawFilter: string | null = search.get(`${prefix}filter`);

  const filter =
    typeof rawFilter === "string"
      ? (JSON.parse(decodeURIComponent(rawFilter)) as Filter)
      : defaultFilter;
  const setFilter = (newFilter: Filter) => {
    trackFilter(newFilter);
    setSearch((prev) => {
      // A new filter changes the result set, so the current page offset no
      // longer applies. Drop it so the list starts again from page 1.
      const params = paramsToObject(prev);
      delete params[`${prefix}page`];
      return {
        ...params,
        [`${prefix}filter`]: encodeURIComponent(JSON.stringify(newFilter)),
      };
    });
  };

  const sort: SortInput = {
    field:
      search.get(`${prefix}sortField`) || defaultSort?.field || "updatedAt",
    order:
      (search.get(`${prefix}sortOrder`) || defaultSort?.order) === "ASC"
        ? "ASC"
        : "DESC",
  };

  const pagination = {
    page: parseInt(
      search.get(`${prefix}page`) || defaultPagination?.page?.toString() || "1"
    ),
    perPage: parseInt(
      search.get(`${prefix}perPage`) ||
        defaultPagination?.perPage?.toString() ||
        "30"
    ),
  };

  const setSort = (sortState: SortInput) => {
    if (name) {
      track("list_sorted", {
        list: name,
        field: sortState.field,
        order: sortState.order === "ASC" ? "ASC" : "DESC",
      });
    }
    setSearch((prev) => ({
      ...paramsToObject(prev),
      [`${prefix}sortField`]: sortState.field,
      [`${prefix}sortOrder`]: sortState.order,
    }));
  };

  const setPage = (page: number) => {
    if (name) {
      track("list_page_changed", { list: name, page });
    }
    setSearch((prev) => ({
      ...paramsToObject(prev),
      [`${prefix}page`]: page.toString(),
    }));
  };
  const setPerPage = (perPage: number) => {
    setSearch((prev) => ({
      ...paramsToObject(prev),
      [`${prefix}perPage`]: perPage.toString(),
    }));
  };

  return {
    sort,
    setSort,
    pagination,
    setPage,
    setPerPage,
    filter,
    setFilter,
  };
}

export default useListVariables;
